import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SetupLayout from './SetupLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const VerifyOTP: React.FC = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');

  useEffect(() => {
    const storedData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    if (!storedData.phone && !storedData.email && !storedData.organization) {
      navigate('/signup');
    }
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0 && !canResend) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    pastedData.split('').forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Read fresh data from sessionStorage
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      
      const response = await axios.post(`${API_URL}/signup/verify-otp`, {
        organizationId: currentData.organizationId,
        phone: currentData.phone,
        otp: otpString,
      });

      if (response.data.success || response.data.data?.success) {
        setSuccess('Verification successful!');
        // Handle nested response structure: response.data.data.data contains organizationId and token
        const responseData = response.data.data?.data || response.data.data || response.data;
        console.log('VerifyOTP response:', response.data);
        console.log('Extracted data:', responseData);
        
        // Save with fresh data to preserve all fields
        sessionStorage.setItem('signupData', JSON.stringify({
          ...currentData,
          verified: true,
          organizationId: responseData.organizationId || currentData.organizationId,
          token: responseData.token || currentData.token,
        }));
        setTimeout(() => navigate('/setup/domain'), 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      // Read fresh data from sessionStorage
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      await axios.post(`${API_URL}/signup/resend-otp`, {
        organizationId: currentData.organizationId,
        phone: currentData.phone,
        token: currentData.token,
      });
      setCountdown(60);
      setCanResend(false);
      setSuccess('OTP sent successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <SetupLayout activeStep={2} backPath="/setup/organization">
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Box sx={{ maxWidth: 450, width: '100%', px: 3, textAlign: 'center' }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 40, color: '#dc2626' }} />
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 1, fontFamily: 'Inter, sans-serif' }}>
            Verify your email
          </Typography>
          <Typography sx={{ color: '#6b7280', mb: 4, fontFamily: 'Inter, sans-serif' }}>
            Enter the 6-digit code sent to <strong>{signupData.email || signupData.phone}</strong>
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

          {/* OTP Input */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 4 }} onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 600, fontFamily: 'Inter, sans-serif' } }}
                sx={{
                  width: 56,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#f9fafb',
                    '& fieldset': { borderColor: digit ? '#dc2626' : '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#dc2626' },
                    '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                  },
                }}
              />
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={loading || otp.join('').length !== 6}
            sx={{ py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, fontSize: '1rem', textTransform: 'none', borderRadius: 2, mb: 3 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Continue'}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
              Didn't receive the code?
            </Typography>
            {canResend ? (
              <Button onClick={handleResend} disabled={resending} startIcon={<RefreshIcon />} sx={{ color: '#dc2626', textTransform: 'none', fontWeight: 600 }}>
                {resending ? 'Sending...' : 'Resend'}
              </Button>
            ) : (
              <Typography sx={{ color: '#dc2626', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Resend in {countdown}s
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default VerifyOTP;
